import { BlogPostTile } from "@components/news/BlogPostTile"
import { LargeBlogTile } from "@components/news/LargeBlogTile"
import { PageHeader } from "@components/page/PageHeader"
import { Select } from "@components/ui/Select"
import { TabControl } from "@components/ui/TabControl"
import { ResultBrowser } from "@components/util/ResultBrowser"
import { useSearchParams } from "eddev/routing"
import { defineView } from "eddev/views"
import { motion } from "motion/react"
import { useMemo } from "react"

export default defineView("archive-blog", (props) => {
  // Query parameters
  const [_, filters] = useSearchParams({
    category: "all",
    year: "all",
  })

  const filterHash = [filters.get("category"), filters.get("year")].join("-")

  // Filtered results
  const results = useMemo(() => {
    const year = filters.get("year")
    const category = filters.get("category")
    return (
      props.blogPosts?.nodes.filter((item) => {
        if (year !== "all" && !item.dateGmt?.startsWith(year)) {
          return false
        }
        if (category !== "all" && item.info?.category?.slug! !== category) {
          return false
        }
        return true
      }) ?? []
    )
  }, [props.blogPosts, filterHash])

  // Build a list of all the cards, so they don't have to be re-rendered on each scroll event
  const cards = useMemo(
    () =>
      results.map((post, i) => {
        if (post === props.blogPosts?.nodes[0]) {
          return (
            <div key={post.uri} className="col-span-12 py-4">
              <LargeBlogTile {...post} />
            </div>
          )
        } else {
          return (
            <div key={post.uri} className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 py-4">
              <BlogPostTile {...post} />
            </div>
          )
        }
      }),
    [results, filterHash],
  )

  // Get a list of all the distinct years
  const yearFilters = useMemo(() => {
    return [...new Set(props.blogPosts?.nodes.map((post) => new Date(post.dateGmt!).getFullYear()))]
      .sort()
      .reverse()
      .map((year) => ({
        type: "item" as const,
        label: year.toString(),
        value: year.toString(),
      }))
  }, [props.blogPosts])

  return (
    <div>
      {/* Page header */}
      <PageHeader
        title="Blog"
        description="Explore our industry news, festival announcements, community features, opinion pieces, and more."
        hasDescription
      />

      <ResultBrowser
        totalItems={results.length}
        loading={false}
        noResults={results.length === 0}
        animationKey={filterHash}
        itemsPerPage={12}
        filters={
          <>
            <Select
              placeholder="Year"
              value={filters.get("year")}
              onValueChange={filters.setter("year")}
              items={[
                {
                  type: "item",
                  label: "All Years",
                  value: "all",
                },
                ...yearFilters,
              ]}
            />
          </>
        }
        tabs={{
          value: filters.get("category"),
          onValueChange: filters.setter("category"),
          items: [
            {
              label: "All",
              value: "all",
            },
            ...(props.blogCategories?.nodes?.map((category) => ({
              label: category.name!,
              value: category.slug!,
            })) ?? []),
          ],
        }}
      >
        {({ page, sensor }) => {
          return (
            <motion.div
              key={[filters.get("category"), filters.get("year")].join("-")}
              className="grid-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {cards.map((card, i) =>
                i < page ? (
                  card
                ) : (
                  <div key={i} className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 h-[300px]">
                    {/* Render the pagination sensor after the last item */}
                    {i === page && sensor}
                  </div>
                ),
              )}
            </motion.div>
          )
        }}
      </ResultBrowser>
    </div>
  )
})
